import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../lib/supabase';
import { PUBLIC_DATA_SOURCES } from '../lib/scoring';
import { useAuth } from '../lib/auth';

// ── DataSources constants ─────────────────────────────────────────
const CATEGORIES = ['All', 'China PP', 'Futures', 'SEA', 'Oil', 'Freight', 'Geopolitical'];

const CAT_COLORS = {
  'China PP': 'blu', 'Futures': 'pur', 'SEA': 'amb', 'Oil': 'red', 'Freight': 'teal', 'Geopolitical': 'grn',
};

// ── DataEntry constants ───────────────────────────────────────────
const EVENTS = [
  { value: 'none', label: 'No new event' },
  { value: 'vp_level_talks', label: 'VP-level talks' },
  { value: 'ship_transited_safely', label: 'Ship transited safely' },
  { value: 'ship_attacked', label: 'Ship attacked' },
  { value: 'vessel_seizure', label: 'Vessel seizure' },
  { value: 'deal_announced', label: 'Peace deal announced' },
  { value: 'talks_collapsed', label: 'Talks collapsed' },
  { value: 'strikes_announced', label: 'Military strikes announced' },
  { value: 'eia_large_draw', label: 'EIA large draw (>5M bbl)' },
];

function Section({ title, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="card mb-12">
      <button onClick={() => setOpen(o => !o)} style={{ width: '100%', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10, marginBottom: open ? 14 : 0 }}>
        <span className="card-title-text" style={{ fontFamily: 'var(--mono)', fontSize: 9, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.1em' }}>{title}</span>
        <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
        <span style={{ color: 'var(--text3)', fontSize: 12 }}>{open ? '▲' : '▼'}</span>
      </button>
      {open && children}
    </div>
  );
}

function FG({ label, hint, badge, children }) {
  return (
    <div className="form-group">
      <div className="form-label">
        <span className="form-label-text">{label}</span>
        {badge && <span className={`badge badge-${badge}`}>{badge.toUpperCase()}</span>}
      </div>
      {children}
      {hint && <div className="form-hint">{hint}</div>}
    </div>
  );
}

const STALE_MS = 20 * 60 * 1000; // 20 minutes — matches LiveTickerStrip freshness threshold

export default function Data({ inputs, updateInput, updateInputs, liveData, addToast }) {
  const { t } = useTranslation();
  const { role } = useAuth();
  const canEdit = role === 'data_updater' || role === 'admin';

  // ── DataSources state ─────────────────────────────────────────
  const [activeCategory, setActiveCategory] = useState('All');
  const [search, setSearch] = useState('');

  // ── DataEntry state ───────────────────────────────────────────
  const [spotStaleDays, setSpotStaleDays] = useState(null);

  useEffect(() => {
    supabase
      .from('pp_map_runs')
      .select('created_at, pp_spot_qingdao')
      .order('created_at', { ascending: false })
      .limit(1)
      .single()
      .then(({ data, error }) => {
        if (error || !data?.created_at) return;
        const days = Math.floor((Date.now() - new Date(data.created_at).getTime()) / 86400000);
        setSpotStaleDays(days);
      });
  }, []);

  // ── DataSources filtered sources ──────────────────────────────
  const filtered = PUBLIC_DATA_SOURCES.filter(s =>
    (activeCategory === 'All' || s.category === activeCategory) &&
    (search === '' || s.name.toLowerCase().includes(search.toLowerCase()) || s.description.toLowerCase().includes(search.toLowerCase()))
  );

  // ── DataEntry helpers ─────────────────────────────────────────
  const getBadge = (fieldKey) => {
    const ts = liveData?.timestamps?.[fieldKey];
    if (!ts || (Date.now() - ts) >= STALE_MS) return 'stale';
    return 'live';
  };

  const kapsIsEmpty = inputs.kaps_stock_mt == null || inputs.kaps_daily_demand_mt == null;
  const kicpIsEmpty = inputs.kicp_portfolio_qty_mt == null && inputs.kicp_gloss18_sell_price == null;
  const n = (key) => ({
    value: inputs[key] ?? '',
    onChange: e => updateInput(key, parseFloat(e.target.value) || 0),
  });
  const s = (key) => ({
    value: inputs[key] ?? '',
    onChange: e => updateInput(key, e.target.value),
  });
  // pct: displays decimal offsets as integer percentages; stores back as decimals
  const pct = (key, defaultDec) => ({
    value: Math.round((inputs[key] != null ? inputs[key] : defaultDec) * 100),
    onChange: e => {
      const raw = parseFloat(e.target.value);
      updateInput(key, Number.isFinite(raw) ? raw / 100 : defaultDec);
    },
  });

  const handleEventToggle = (val) => {
    const current = inputs.recent_events || [];
    if (val === 'none') {
      updateInput('recent_events', ['none']);
      return;
    }
    const without = current.filter(e => e !== 'none');
    if (without.includes(val)) {
      updateInput('recent_events', without.filter(e => e !== val));
    } else {
      updateInput('recent_events', [...without, val]);
    }
  };

  return (
    <div>

      {/* ═══════════════════════════════════════════════════════════
          A — DATA SOURCES (all roles)
          ═══════════════════════════════════════════════════════════ */}

      <div className="page-hdr">
        <h2>Public Data Sources</h2>
        <p>All external data sources used in PP MAP v5. Click to open in a new tab. Update your model inputs using these sources weekly (or daily for oil and PP spot). Sources verified May 2026.</p>
      </div>

      {/* Update frequency guide */}
      <div className="card mb-16">
        <div className="card-title"><span className="card-title-text">Recommended Update Frequency</span></div>
        <div className="g3">
          {[
            { freq: 'Daily', color: 'red', items: ['WTI / Brent oil price', 'PPH-F03Q Qingdao (call supplier)', 'Geopolitical events / Hormuz status', 'SCI99 propylene spot'] },
            { freq: 'Weekly', color: 'amb', items: ['Zhenhai / Jiujiang / Daqing prices (生意社)', 'SunSirs PP benchmark', 'DCE PP M3 Futures (manual — dce.com.cn; M1 is auto-fetched)', 'CPCIF polymer inventory + op rates', 'SEA CFR prices (Polymerupdate)', 'BOPP downstream op rate (CCFGroup)', 'EIA petroleum supply report (Wednesday)'] },
            { freq: 'As needed', color: 'grn', items: ['VLCC freight rates (Baltic Exchange)', 'Naphtha crack spread (Argus/Platts)', 'Polymarket prediction market cross-check', 'ICIS plant news and disruptions', 'Argus Global Polypropylene report'] },
          ].map(f => (
            <div key={f.freq} className="card" style={{ borderTop: `3px solid var(--${f.color})` }}>
              <div className="label-mono mb-8" style={{ color: `var(--${f.color})` }}>{f.freq}</div>
              <ul style={{ paddingLeft: 14, fontSize: 11, color: 'var(--text2)', lineHeight: 1.8 }}>
                {f.items.map(i => <li key={i}>{i}</li>)}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* Filter bar */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          type="text"
          className="form-input"
          placeholder="Search sources..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ maxWidth: 220 }}
        />
        <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
          {CATEGORIES.map(c => (
            <button key={c} onClick={() => setActiveCategory(c)} style={{
              padding: '5px 10px', borderRadius: 5, border: '1px solid',
              fontFamily: 'var(--mono)', fontSize: 10, fontWeight: 700, cursor: 'pointer', transition: 'all .15s',
              background: activeCategory === c ? 'var(--blu)' : 'var(--surface)',
              borderColor: activeCategory === c ? 'var(--blu)' : 'var(--border2)',
              color: activeCategory === c ? 'white' : 'var(--text2)',
            }}>
              {c} {c !== 'All' && <span style={{ opacity: .7 }}>({PUBLIC_DATA_SOURCES.filter(s => s.category === c).length})</span>}
            </button>
          ))}
        </div>
      </div>

      {/* Auto-fetch callout */}
      <div className="alert alert-blu" style={{ marginBottom: 14 }}>
        <span className="alert-icon">⚡</span>
        <div className="alert-body">
          <div><strong>Auto-fetched every 15 min:</strong> WTI · Brent · DCE PP M1 · Polymarket — via <span style={{ fontFamily: 'var(--mono)', fontSize: 11 }}>/api/live-prices</span> (internal proxy) · USD/CNY — via <span style={{ fontFamily: 'var(--mono)', fontSize: 11 }}>open.er-api.com</span></div>
          <div style={{ fontSize: 11, marginTop: 3, opacity: 0.8 }}>每15分钟自动拉取：WTI · Brent · DCE M1 · Polymarket（经 /api/live-prices 代理）· USD/CNY（open.er-api.com）</div>
        </div>
      </div>

      {/* Source Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 10 }}>
        {filtered.map(source => {
          const color = CAT_COLORS[source.category] || 'blu';
          return (
            <a
              key={source.name}
              href={source.url}
              target="_blank"
              rel="noopener noreferrer"
              style={{ textDecoration: 'none' }}
            >
              <div style={{
                background: 'var(--surface)', border: `1px solid var(--border)`,
                borderTop: `3px solid var(--${color})`,
                borderRadius: 'var(--radius)', padding: '13px 15px',
                boxShadow: 'var(--shadow)', cursor: 'pointer',
                transition: 'all .15s', height: '100%',
              }}
                onMouseEnter={e => { e.currentTarget.style.boxShadow = 'var(--shadow2)'; e.currentTarget.style.borderColor = `var(--${color}-b, var(--border2))`; }}
                onMouseLeave={e => { e.currentTarget.style.boxShadow = 'var(--shadow)'; e.currentTarget.style.borderColor = 'var(--border)'; }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, marginBottom: 6 }}>
                  <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--text)', lineHeight: 1.3 }}>{source.name}</div>
                  <span className={`badge badge-${color}`} style={{ flexShrink: 0 }}>{source.category}</span>
                </div>
                <div style={{ fontSize: 11, color: 'var(--text2)', lineHeight: 1.5, marginBottom: 8 }}>{source.description}</div>
                <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: `var(--${color})`, display: 'flex', alignItems: 'center', gap: 4 }}>
                  <span>↗</span>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 220 }}>{source.url.replace('https://', '').replace('http://', '')}</span>
                </div>
              </div>
            </a>
          );
        })}
      </div>

      {filtered.length === 0 && (
        <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--text3)' }}>
          No sources found matching your search.
        </div>
      )}

      {/* Manual call reminder */}
      <div className="card" style={{ marginTop: 16 }}>
        <div className="card-title"><span className="card-title-text">Manual Data Requiring Phone/Email Contact</span></div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 10 }}>
          {[
            { title: 'PPH-F03Q Spot Price', action: 'Call Qingdao Refinery', urgency: 'Daily — most important input', color: 'red' },
            { title: 'Supplier Allocation Status', action: 'Call Qingdao Refinery', urgency: 'Daily during tight supply', color: 'red' },
            { title: 'Conversion Cost Confirmation', action: 'Contact Finance Team', urgency: 'One-time — still at ¥1,800 estimate', color: 'amb' },
            { title: 'KAPS Dashboard Refresh', action: 'Access 172.16.10.28', urgency: 'Daily for stock accuracy', color: 'amb' },
            { title: 'KICP Film Orders', action: 'Access m.kunlene-sz.com:8218', urgency: 'Weekly for OTI calculation', color: 'grn' },
            { title: 'Jiujiang/Daqing Trial Qualification', action: 'Contact R&D team', urgency: 'Start 30-day trial for cost savings', color: 'grn' },
          ].map(m => (
            <div key={m.title} style={{ padding: '11px 13px', background: 'var(--surface2)', borderRadius: 7, border: `1px solid var(--border)`, borderLeft: `3px solid var(--${m.color})` }}>
              <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 3 }}>{m.title}</div>
              <div style={{ fontSize: 11, color: `var(--${m.color})`, fontWeight: 500 }}>{m.action}</div>
              <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 2 }}>{m.urgency}</div>
            </div>
          ))}
        </div>
        <div style={{ marginTop: 10 }} className="alert alert-amb">
          <span className="alert-icon">🔧</span>
          <div className="alert-body">
            <div>API integration in progress — IT team is implementing server endpoints. Use direct system access until Settings API is confirmed working: KAPS at 172.16.10.28, KICP at m.kunlene-sz.com:8218.</div>
            <div style={{ fontSize: 11, marginTop: 3, opacity: 0.8 }}>API 集成进行中 — IT 团队正在实施服务器端点。在设置页面 API 确认可用之前，请使用直接系统访问：KAPS 地址 172.16.10.28，KICP 地址 m.kunlene-sz.com:8218。</div>
          </div>
        </div>
      </div>

      {/* ── Divider ─────────────────────────────────────────────── */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 14,
        margin: '32px 0 24px',
        borderTop: '2px solid var(--border)',
        paddingTop: 20,
      }}>
        <div>
          <div style={{ fontFamily: 'var(--head)', fontSize: 17, fontWeight: 800, color: 'var(--t0)', letterSpacing: '-.01em' }}>
            Manual Data Entry
          </div>
          <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--t3)', marginTop: 2 }}>
            手动数据录入
          </div>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════
          B — MANUAL DATA ENTRY (data_updater + admin only)
          ═══════════════════════════════════════════════════════════ */}

      {canEdit ? (
        <div>
          <p style={{ fontSize: 13, color: 'var(--t2)', marginBottom: 16 }}>
            Enter latest market data. All inputs auto-save locally and update the procurement model. Connect KAPS/KICP in Settings for real-time internal data.
          </p>

          {/* Oil & Geopolitical */}
          <Section title="🛢️ Oil & Geopolitical">
            <div className="g3">
              <FG label="WTI Price ($/bbl)" badge={getBadge('wti_price')}>
                <input type="number" className="form-input" step="0.01" {...n('wti_price')} />
              </FG>
              <FG label="Brent Price ($/bbl)" badge={getBadge('brent_price')}>
                <input type="number" className="form-input" step="0.01" {...n('brent_price')} />
              </FG>
              <FG label="USD/CNY Rate" badge={getBadge('currency_rate')}>
                <input type="number" className="form-input" step="0.001" {...n('currency_rate')} />
              </FG>
            </div>
            <div className="g3">
              <FG label="Hormuz Traffic (% of normal)" hint="5% = almost closed, 100% = fully open" badge="manual">
                <input type="number" className="form-input" min="0" max="100" {...n('hormuz_traffic_pct')} />
                <input type="range" min="0" max="100" value={inputs.hormuz_traffic_pct || 0} onChange={e => updateInput('hormuz_traffic_pct', parseFloat(e.target.value))} style={{ marginTop: 6 }} />
              </FG>
              <FG label="Hormuz Status" badge="manual">
                <div className="radio-group">
                  {['open','partial','closed'].map(v => (
                    <div key={v} className="radio-opt">
                      <input type="radio" id={`hs-${v}`} name="hormuz_status" value={v} checked={inputs.hormuz_status === v} onChange={() => updateInput('hormuz_status', v)} />
                      <label htmlFor={`hs-${v}`}>{v.charAt(0).toUpperCase() + v.slice(1)}</label>
                    </div>
                  ))}
                </div>
              </FG>
              <FG label="VLCC Rate Premium (% above normal)" hint="0% = normal freight, 100% = doubled, 200% = tripled" badge="est">
                <input type="number" className="form-input" min="0" {...n('vlcc_rate_premium')} />
              </FG>
            </div>
            <div className="g2">
              <FG label="Conflict Duration (weeks)" badge="manual">
                <input type="number" className="form-input" min="0" {...n('conflict_weeks')} />
              </FG>
              <FG label="Polymarket Peace Probability (0–1)" hint="Optional: from Polymarket.com — leave blank to use Bayesian only" badge={getBadge('polymarket_peace')}>
                <input type="number" className="form-input" min="0" max="1" step="0.01" value={inputs.polymarket_peace ?? ''} onChange={e => updateInput('polymarket_peace', e.target.value === '' ? null : parseFloat(e.target.value))} />
              </FG>
            </div>
            <FG label="Recent Geopolitical Events (select all that apply)" hint="Each event updates the Bayesian probability matrix" badge="manual">
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
                {EVENTS.map(ev => {
                  const active = (inputs.recent_events || []).includes(ev.value);
                  return (
                    <button key={ev.value} onClick={() => handleEventToggle(ev.value)} style={{
                      padding: '5px 10px', borderRadius: 6, border: '1px solid',
                      background: active ? 'var(--blu)' : 'var(--surface)',
                      borderColor: active ? 'var(--blu)' : 'var(--border2)',
                      color: active ? 'white' : 'var(--text2)',
                      fontFamily: 'var(--mono)', fontSize: 10, fontWeight: 700, cursor: 'pointer', transition: 'all .15s',
                    }}>
                      {ev.label}
                    </button>
                  );
                })}
              </div>
            </FG>
            <FG label="Event Description (free text)">
              <textarea className="form-input" rows={2} {...s('geopolitical_event')} />
            </FG>
          </Section>

          {/* China PP Prices */}
          <Section title="🇨🇳 China PP Domestic Prices">
            <div style={{ marginBottom: 10 }} className="alert alert-blu">
              <span className="alert-icon">ℹ️</span>
              <div className="alert-body">Source: SCI99 (sci99.com) · 生意社 (sunsirs.com) · Update daily before running model</div>
            </div>
            <div className="g3">
              <FG label="PPH-F03Q Qingdao (¥/MT)" hint="Your primary grade — call supplier daily" badge="manual">
                <input type="number" className="form-input" step="50" {...n('pp_spot_qingdao')} />
                {spotStaleDays > 3 && (
                  <div className="alert alert-red" style={{ marginTop: 6, padding: '6px 10px', fontSize: 11 }}>
                    <span className="alert-icon">⚠️</span>
                    <div className="alert-body">
                      <div>Last updated {spotStaleDays} days ago — verify with supplier before running</div>
                      <div style={{ marginTop: 2, opacity: 0.8 }}>已 {spotStaleDays} 天未更新——运行前请向供应商确认</div>
                    </div>
                  </div>
                )}
              </FG>
              <FG label="Zhenhai T30S (¥/MT)" hint="Key domestic benchmark — 生意社" badge="manual">
                <input type="number" className="form-input" step="50" {...n('pp_spot_zhenhai')} />
              </FG>
              <FG label="Jiujiang T30S (¥/MT)" hint="Potential backup grade — 生意社" badge="manual">
                <input type="number" className="form-input" step="50" {...n('pp_spot_jiujiang')} />
              </FG>
              <FG label="Daqing T30S (¥/MT)" badge="manual">
                <input type="number" className="form-input" step="50" {...n('pp_spot_daqing')} />
              </FG>
              <FG label="Fushun L5E89 (¥/MT)" badge="manual">
                <input type="number" className="form-input" step="50" {...n('pp_spot_fushun')} />
              </FG>
              <FG label="Propylene Spot (¥/MT)" hint="SCI99 daily · drives propylene-PP spread (STI)" badge="manual">
                <input type="number" className="form-input" step="50" {...n('propylene_spot')} />
              </FG>
            </div>
            <div className="g3">
              <FG label="DCE PP M1 Futures (¥/MT)" hint="Dalian Commodity Exchange near month" badge={getBadge('dce_futures_m1')}>
                <input type="number" className="form-input" step="50" {...n('dce_futures_m1')} />
              </FG>
              <FG label="DCE PP M3 Futures (¥/MT)" hint="3-month contract — for forward curve shape" badge="manual">
                <input type="number" className="form-input" step="50" {...n('dce_futures_m3')} />
              </FG>
              <FG label="Naphtha Crack Spread ($/MT)" hint="Argus/Platts CFR Japan naphtha vs Brent" badge="manual">
                <input type="number" className="form-input" step="5" {...n('naphtha_crack')} />
              </FG>
            </div>
            <div className="g3">
              <FG label="China PP Plant Op Rate (%)" hint="SunSirs weekly — Monday publication" badge="manual">
                <input type="number" className="form-input" min="0" max="100" {...n('plant_op_rate')} />
              </FG>
              <FG label="China Export Cushion Active" hint="Check if China PP exports are record-high">
                <div className="radio-group">
                  <div className="radio-opt">
                    <input type="radio" id="exp-yes" name="china_export" checked={inputs.china_export_cushion === true} onChange={() => updateInput('china_export_cushion', true)} />
                    <label htmlFor="exp-yes">Yes (−5 STI)</label>
                  </div>
                  <div className="radio-opt">
                    <input type="radio" id="exp-no" name="china_export" checked={inputs.china_export_cushion === false} onChange={() => updateInput('china_export_cushion', false)} />
                    <label htmlFor="exp-no">No</label>
                  </div>
                </div>
              </FG>
            </div>
          </Section>

          {/* Market Stress Inputs */}
          <Section title="📉 Market Stress Indicators">
            <div className="g2">
              <FG label="Import Landed Cost (¥/MT)" hint="China spot vs what Middle East PP would cost landed — drives import arb" badge="est">
                <input type="number" className="form-input" step="50" {...n('import_landed_cost')} />
              </FG>
              <FG label="BOPP Downstream Op Rate (%)" hint="Weekly from CPCIF / CCFGroup / Argus" badge="manual">
                <input type="number" className="form-input" min="0" max="100" {...n('bopp_op_rate')} />
              </FG>
            </div>
            <FG label="Supplier Allocation Status" hint="Confirmed directly with Qingdao Refinery — drives MSI ±15–25 pts" badge="manual">
              <div className="radio-group">
                {['open','firm','restricted','suspended'].map(v => (
                  <div key={v} className="radio-opt">
                    <input type="radio" id={`sa-${v}`} name="supplier_allocation" value={v} checked={inputs.supplier_allocation === v} onChange={() => updateInput('supplier_allocation', v)} />
                    <label htmlFor={`sa-${v}`} style={{ color: v === 'restricted' ? 'var(--amb)' : v === 'suspended' ? 'var(--red)' : undefined }}>
                      {v.charAt(0).toUpperCase() + v.slice(1)}
                    </label>
                  </div>
                ))}
              </div>
            </FG>
          </Section>

          {/* SEA Prices */}
          <Section title="🌏 SEA Regional PP Prices">
            <div style={{ marginBottom: 10 }} className="alert alert-blu">
              <span className="alert-icon">ℹ️</span>
              <div className="alert-body">Source: Polymerupdate.com (weekly CFR) · Argus Global Polypropylene report · Update weekly</div>
            </div>
            <div className="g2">
              <FG label="SEA BOPP CFR Low ($/MT)" badge="manual">
                <input type="number" className="form-input" step="10" {...n('sea_bopp_cfr_low')} />
              </FG>
              <FG label="SEA BOPP CFR High ($/MT)" badge="manual">
                <input type="number" className="form-input" step="10" {...n('sea_bopp_cfr_high')} />
              </FG>
              <FG label="SEA Film CFR Low ($/MT)" badge="manual">
                <input type="number" className="form-input" step="10" {...n('sea_film_cfr_low')} />
              </FG>
              <FG label="SEA Film CFR High ($/MT)" badge="manual">
                <input type="number" className="form-input" step="10" {...n('sea_film_cfr_high')} />
              </FG>
            </div>
          </Section>

          {/* KAPS Data */}
          <Section title="🏭 KAPS MRP — Internal Inventory (172.16.10.28)">
            {kapsIsEmpty && (
              <div style={{ marginBottom: 10 }} className="alert alert-amb">
                <span className="alert-icon">⚠️</span>
                <div className="alert-body">{t('dataentry.kaps_no_data_banner')}</div>
              </div>
            )}
            <div style={{ marginBottom: 10 }} className="alert alert-amb">
              <span className="alert-icon">🔧</span>
              <div className="alert-body">
                <div>API integration in progress — IT team is implementing server endpoints. Use direct system access until Settings API is confirmed working: KAPS at 172.16.10.28, KICP at m.kunlene-sz.com:8218.</div>
                <div style={{ fontSize: 11, marginTop: 3, opacity: 0.8 }}>API 集成进行中 — IT 团队正在实施服务器端点。在设置页面 API 确认可用之前，请使用直接系统访问：KAPS 地址 172.16.10.28，KICP 地址 m.kunlene-sz.com:8218。</div>
              </div>
            </div>
            <div style={{ marginBottom: 10 }} className="alert alert-grn">
              <span className="alert-icon">🔗</span>
              <div className="alert-body">Configure KAPS API endpoint in Settings for one-click refresh. Manual entry below.</div>
            </div>
            <div className="g3">
              <FG label="Realtime Stock (MT)" hint="Non-bonded only" badge="manual">
                <input type="number" className="form-input" step="1" {...n('kaps_stock_mt')} />
              </FG>
              <FG label="Bonded Stock (MT)" badge="manual">
                <input type="number" className="form-input" step="1" {...n('kaps_stock_bonded_mt')} />
              </FG>
              <FG label="In-Transit (MT)" badge="manual">
                <input type="number" className="form-input" step="1" {...n('kaps_transit_mt')} />
              </FG>
              <FG label="PR Confirmed (MT)" hint="Filtered by delivery date within 30 days" badge="manual">
                <input type="number" className="form-input" step="1" {...n('kaps_pr_mt')} />
              </FG>
              <FG label="Weighted Avg Cost (¥/MT)" hint="NOT the same as current market price" badge="manual">
                <input type="number" className="form-input" step="1" {...n('kaps_avg_cost')} />
              </FG>
              <FG label="Safety Stock Level (MT)" badge="manual">
                <input type="number" className="form-input" step="1" {...n('kaps_safety_stock_mt')} />
              </FG>
              <FG label="Daily Demand (MT/day)" hint="Rolling 30-day planned demand ÷ 30" badge="manual">
                <input type="number" className="form-input" step="0.1" {...n('kaps_daily_demand_mt')} />
              </FG>
              <FG label="Lead Time (days)" hint="Supplier order → warehouse receipt" badge="manual">
                <input type="number" className="form-input" min="1" max="60" {...n('lead_time_days')} />
              </FG>
            </div>
          </Section>

          {/* KICP Data */}
          <Section title="📦 KICP — Film Orders (m.kunlene-sz.com:8218)">
            {kicpIsEmpty && (
              <div style={{ marginBottom: 10 }} className="alert alert-amb">
                <span className="alert-icon">⚠️</span>
                <div className="alert-body">{t('dataentry.kicp_no_data_banner')}</div>
              </div>
            )}
            <div style={{ marginBottom: 10 }} className="alert alert-amb">
              <span className="alert-icon">🔧</span>
              <div className="alert-body">
                <div>API integration in progress — IT team is implementing server endpoints. Use direct system access until Settings API is confirmed working: KAPS at 172.16.10.28, KICP at m.kunlene-sz.com:8218.</div>
                <div style={{ fontSize: 11, marginTop: 3, opacity: 0.8 }}>API 集成进行中 — IT 团队正在实施服务器端点。在设置页面 API 确认可用之前，请使用直接系统访问：KAPS 地址 172.16.10.28，KICP 地址 m.kunlene-sz.com:8218。</div>
              </div>
            </div>
            <div style={{ marginBottom: 10 }} className="alert alert-grn">
              <span className="alert-icon">🔗</span>
              <div className="alert-body">Configure KICP API in Settings for auto-refresh. Manual entry below for 已下订单 data.</div>
            </div>
            <div className="g2">
              <FG label="Gloss 18µm Sell Price (¥/MT)" badge="manual">
                <input type="number" className="form-input" step="50" {...n('kicp_gloss18_sell_price')} />
              </FG>
              <FG label="Gloss 18µm Volume (MT)" badge="manual">
                <input type="number" className="form-input" step="1" {...n('kicp_gloss18_qty_mt')} />
              </FG>
              <FG label="Portfolio Weighted Avg Sell (¥/MT)" hint="All film types — for margin calculation" badge="manual">
                <input type="number" className="form-input" step="50" {...n('kicp_portfolio_avg_sell')} />
              </FG>
              <FG label="Portfolio Total Volume (MT)" hint="Current month 已下订单 confirmed qty" badge="manual">
                <input type="number" className="form-input" step="1" {...n('kicp_portfolio_qty_mt')} />
              </FG>
              <FG label="Prior Month Volume (MT)" hint="For OTI volume momentum calculation" badge="manual">
                <input type="number" className="form-input" step="1" {...n('kicp_portfolio_qty_prev_mt')} />
              </FG>
              <FG label="Prior Month Avg Sell (¥/MT)" hint="For OTI price momentum calculation" badge="manual">
                <input type="number" className="form-input" step="50" {...n('kicp_portfolio_avg_sell_prev')} />
              </FG>
              <FG label="Conversion Cost (¥/MT)" hint="Finance team confirmed — ¥1,800 interim if not confirmed" badge="manual">
                <input type="number" className="form-input" step="50" {...n('conversion_cost')} />
              </FG>
              <FG label="Net Revision % (+ = increase, − = cancellation)" hint="Order cancellations/revisions — drives OTI C component" badge="manual">
                <input type="number" className="form-input" step="0.5" {...n('net_revision_pct')} />
              </FG>
            </div>
            <div className="g2">
              <FG label="PP Yield Ratio (film MT / PP MT)" hint="Typically 1.03 for BOPP film — confirm with production team" badge="manual">
                <input type="number" className="form-input" step="0.01" min="1" max="1.1" {...n('pp_yield_ratio')} />
              </FG>
            </div>
          </Section>

          {/* Scenario Calibration */}
          <Section title={t('scenario_calibration.section_title')} defaultOpen={false}>
            <div style={{ marginBottom: 10 }} className="alert alert-blu">
              <span className="alert-icon">ℹ️</span>
              <div className="alert-body">{t('scenario_calibration.helper_text')}</div>
            </div>
            {[
              { key: 'peace',      label: t('scenario_calibration.peace_label'),      defaults: [0.92, 0.97, 1.02] },
              { key: 'stalemate',  label: t('scenario_calibration.stalemate_label'),  defaults: [1.00, 1.05, 1.10] },
              { key: 'escalation', label: t('scenario_calibration.escalation_label'), defaults: [1.10, 1.20, 1.30] },
              { key: 'strike',     label: t('scenario_calibration.strike_label'),     defaults: [1.30, 1.50, 1.70] },
            ].map(({ key, label, defaults }) => (
              <div key={key} style={{ marginBottom: 16 }}>
                <div style={{ fontFamily: 'var(--mono)', fontSize: 10, fontWeight: 700, color: 'var(--t3)', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 8 }}>{label}</div>
                <div className="g3">
                  <FG label={`${label} · ${t('scenario_calibration.low')}`} hint="% of spot">
                    <input type="number" className="form-input" min="50" max="300" step="1" {...pct(`scenario_${key}_low`, defaults[0])} />
                  </FG>
                  <FG label={`${label} · ${t('scenario_calibration.mid')}`} hint="% of spot">
                    <input type="number" className="form-input" min="50" max="300" step="1" {...pct(`scenario_${key}_mid`, defaults[1])} />
                  </FG>
                  <FG label={`${label} · ${t('scenario_calibration.high')}`} hint="% of spot">
                    <input type="number" className="form-input" min="50" max="300" step="1" {...pct(`scenario_${key}_high`, defaults[2])} />
                  </FG>
                </div>
              </div>
            ))}
          </Section>
        </div>
      ) : (
        <div className="card" style={{ textAlign: 'center', padding: '32px 20px', color: 'var(--t3)' }}>
          <div style={{ fontSize: 14, marginBottom: 6 }}>Data entry requires the Data Updater role.</div>
          <div style={{ fontSize: 12, opacity: 0.7 }}>数据录入需要数据更新员权限。</div>
        </div>
      )}

    </div>
  );
}
